/**
 * The onboarding tours, and the controls each step points at.
 *
 * Tours are not tied to routes. Each one arms when its own anchors are on the
 * page, so the shell tour runs wherever the user first lands and the table tour
 * runs on the first page that has a table — network, orders, styles, whichever
 * they open first. Adding a route never means remembering to register it here.
 *
 * `target` is a `data-tour` attribute value on the real control. Copy lives in
 * `shared.json` under `onboardingTour.steps.<key>`.
 */

export interface TourStepDef {
  key: string;
  target: string;
  side: 'top' | 'bottom' | 'left' | 'right';
  /**
   * Centre the card in the viewport instead of anchoring it beside the target.
   *
   * For a step whose target is a whole surface — a table, a page region — there
   * is no useful side to sit on: the anchor fills the screen, so the card gets
   * pushed off it. The ring still shows what is being described.
   */
  centered?: boolean;
}

export interface TourDef {
  id: string;
  steps: TourStepDef[];
}

/** The app shell: navigation and the header controls, in header order. */
const SHELL_TOUR: TourDef = {
  id: 'shell',
  steps: [
    { key: 'sidebar', target: 'sidebar', side: 'right' },
    { key: 'sidebarToggle', target: 'sidebar-toggle', side: 'right' },
    { key: 'aiProcessing', target: 'ai-processing', side: 'bottom' },
    { key: 'uploads', target: 'uploads', side: 'bottom' },
    { key: 'notifications', target: 'notifications', side: 'bottom' },
    { key: 'help', target: 'help', side: 'bottom' },
    { key: 'theme', target: 'theme', side: 'bottom' },
    { key: 'profile', target: 'profile', side: 'bottom' },
  ],
};

/**
 * Page structure, which every feature area shares: the tab row that splits a page
 * into sections, and the primary action in its toolbar.
 *
 * Deliberately generic. Every area is reached the same way, so one tour anchored
 * on the shared components covers all of them — rather than twenty near-identical
 * per-feature tours saying "this page is a table".
 */
const PAGE_TOUR: TourDef = {
  id: 'page',
  steps: [
    { key: 'pageTabs', target: 'page-tabs', side: 'bottom' },
    { key: 'pageAction', target: 'page-action', side: 'bottom' },
  ],
};

const COMPARE_TOUR: TourDef = {
  id: 'scmCompare',
  steps: [
    {
      key: 'compareOrderPicker',
      target: 'compare-order-picker',
      side: 'bottom',
    },
    { key: 'compareResults', target: 'compare-results', side: 'bottom' },
    { key: 'compareView', target: 'compare-view', side: 'bottom' },
    { key: 'compareLegend', target: 'compare-legend', side: 'left' },
    { key: 'compareRotate', target: 'compare-rotate', side: 'right' },
    { key: 'compareAnalysis', target: 'compare-analysis', side: 'left' },
  ],
};

/**
 * The tracing and mapping graphs — supply chain, orders, fibre transactions.
 * Unlike a table, nothing about a graph says it can be panned, zoomed or
 * expanded.
 */
const GRAPH_TOUR: TourDef = {
  id: 'graph',
  steps: [{ key: 'graph', target: 'graph', side: 'top', centered: true }],
};

/**
 * The data table, which is where most of the work happens and where the least is
 * discoverable — bulk selection, in-place editing and per-row actions all look
 * like plain text until hovered or clicked.
 */
const TABLE_TOUR: TourDef = {
  id: 'table',
  steps: [
    { key: 'tableSelect', target: 'table-select', side: 'right' },
    { key: 'tableEdit', target: 'table-edit', side: 'top' },
    { key: 'tableActions', target: 'table-actions', side: 'left' },
    // Introduce the toolbar as a group before naming its individual controls,
    // so the user knows where everything moved to before being shown each one.
    { key: 'tableToolbar', target: 'table-toolbar', side: 'bottom' },
    { key: 'tableFilter', target: 'table-filter', side: 'bottom' },
    { key: 'tableSettings', target: 'table-settings', side: 'bottom' },
    // The header is four separate affordances, not one: each step rings the
    // control it describes instead of the whole cell.
    { key: 'tableSort', target: 'table-sort', side: 'bottom' },
    { key: 'tableColumnFilter', target: 'table-column-filter', side: 'bottom' },
    { key: 'tableResize', target: 'table-resize', side: 'bottom' },
    { key: 'tableReorder', target: 'table-reorder', side: 'bottom' },
  ],
};

/**
 * Documents, where the table doubles as a drop target — nothing on screen says
 * so, and the alternative is hunting for an upload button.
 */
const DOCUMENTS_TOUR: TourDef = {
  id: 'documents',
  steps: [
    {
      key: 'documentsDrop',
      target: 'documents-drop',
      side: 'top',
      // The target is the whole table, so there is no side to sit beside.
      centered: true,
    },
  ],
};

/**
 * The collaboration review flow on a supply chain: submitting a mapping for
 * review, and — on the reviewing side — finding what is waiting. Every control
 * is a small icon or badge in the toolbar or on a node, so none of it reads as
 * clickable.
 *
 * Which steps a user gets follows what they can do: a collaborator has the task
 * button, a reviewer has the pending-review badge and the node statuses. The
 * anchors the role lacks simply are not on the page, so those steps drop.
 */
const SUPPLY_CHAIN_REVIEW_TOUR: TourDef = {
  id: 'supplyChainReview',
  steps: [
    { key: 'scmViewTask', target: 'scm-view-task', side: 'bottom' },
    { key: 'scmReviewCounts', target: 'scm-review-counts', side: 'bottom' },
    { key: 'scmNodeStatus', target: 'scm-node-status', side: 'right' },
  ],
};

/**
 * The same review flow on a product mapping. Separate from the supply chain tour
 * rather than shared with it: the two boards have their own controls in their own
 * places, so a single tour would point at anchors that only exist on one of them
 * and silently shrink to whichever page it armed on.
 */
const PRODUCT_MAPPING_REVIEW_TOUR: TourDef = {
  id: 'productMappingReview',
  steps: [
    { key: 'cpmViewTask', target: 'cpm-view-task', side: 'bottom' },
    { key: 'cpmReviewCounts', target: 'cpm-review-counts', side: 'bottom' },
    { key: 'cpmNodeStatus', target: 'cpm-node-status', side: 'right' },
  ],
};

/**
 * Order matters: the first tour that is unfinished and has anchors present is the
 * one that runs.
 *
 * The shell comes first, so navigation is explained before anything in the page.
 * Documents precedes the generic table tour so that landing on the documents list
 * teaches the thing unique to it; the table tour then runs on the next table page
 * (only one tour runs per page visit).
 *
 * The two review tours come last for the same reason: the graph tour explains how
 * to move around a board, which is worth knowing before the controls sitting on
 * it. They never compete — each one's anchors exist on only one of the two
 * boards.
 */
export const TOURS: TourDef[] = [
  SHELL_TOUR,
  PAGE_TOUR,
  COMPARE_TOUR,
  DOCUMENTS_TOUR,
  TABLE_TOUR,
  GRAPH_TOUR,
  SUPPLY_CHAIN_REVIEW_TOUR,
  PRODUCT_MAPPING_REVIEW_TOUR,
];
